'use client';

import { Box } from '@mui/material';
import { HospitalListingItem } from '../HospitalSearchResults/HospitalListingItem';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import {
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_responseFilter,
  ibow2_proto_RequestUser,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { Dispatch, SetStateAction } from 'react';

// リストの参照タイプ定義
export type HospitalListingListRef = {
  scrollToListing: (portalOfficeId: number, source?: 'click' | 'hover') => void;
};

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalSearchFilters?: api_portalOfficeHospitalListing_responseFilter[];
  hospitalListings: HospitalListingOutput[];
  isPublicSearch: boolean;
  requestUser?: ibow2_proto_RequestUser;
  localFavorites: Array<ibow2_proto_RequestUserFavorite>;
  setLocalFavorites: Dispatch<
    SetStateAction<ibow2_proto_RequestUserFavorite[]>
  >;
  getCustomLink: (listing: HospitalListingOutput) => string | undefined;
  mapMarkerPortalOfficeId?: number | null;
  setMapMarkerPortalOfficeIdWithoutScroll?: (
    portalOfficeId: number | null,
  ) => void;
  isShowMap?: boolean;
};

export const HospitalSearchResults = forwardRef<HospitalListingListRef, Props>(
  function HospitalSearchResults(
    {
      hospitalMedicalSubjects,
      hospitalSearchFilters,
      hospitalListings,
      isPublicSearch,
      requestUser,
      localFavorites,
      setLocalFavorites,
      getCustomLink,
      mapMarkerPortalOfficeId,
      setMapMarkerPortalOfficeIdWithoutScroll,
      isShowMap = false,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastSelectedIdRef = useRef<number | null>(null); // ちらつき防止用
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // デバウンス処理用
    const isMarkerClickScrollRef = useRef<boolean>(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // スクロールコンテナを見つける関数
    const findScrollContainer = useCallback(() => {
      if (containerRef.current) {
        let parent = containerRef.current.parentElement;
        while (parent && parent !== document.body) {
          const computedStyle = window.getComputedStyle(parent);
          const overflow = computedStyle.overflow || computedStyle.overflowY;

          if (overflow === 'auto' || overflow === 'scroll') {
            return parent;
          }
          parent = parent.parentElement;
        }
      }
      return null;
    }, []);

    // 一番下のアイテム選択を試行
    const trySelectBottomItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        let isAtBottom = false;
        const scrollContainer = findScrollContainer();

        if (scrollContainer) {
          isAtBottom =
            scrollContainer.scrollTop + scrollContainer.clientHeight >=
            scrollContainer.scrollHeight - 5; // 5pxの余裕を持たせる
        } else {
          // フォールバック: ページ全体のスクロール位置をチェック
          isAtBottom =
            window.scrollY + window.innerHeight >=
            document.documentElement.scrollHeight - 5;
        }

        if (!isAtBottom || !hospitalListings || hospitalListings.length === 0) {
          return null;
        }

        const lastHospital = hospitalListings[hospitalListings.length - 1];
        if (!lastHospital.portal_office_id) return null;

        const lastElement = containerRef.current?.querySelector(
          `[data-hospital-id="${lastHospital.portal_office_id}"]`,
        );
        if (!lastElement) return null;

        const rect = lastElement.getBoundingClientRect();
        const isVisible =
          rect.bottom > containerRect.top && rect.top < containerRect.bottom;

        return isVisible ? lastHospital.portal_office_id : null;
      },
      [hospitalListings, findScrollContainer],
    );

    // 一番上の完全表示アイテムを選択
    const findTopCompletelyVisibleItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        if (!containerRef.current || !hospitalListings) return null;

        for (const hospital of hospitalListings) {
          if (!hospital.portal_office_id) continue;

          const element = containerRef.current.querySelector(
            `[data-hospital-id="${hospital.portal_office_id}"]`,
          );
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const isCompletelyVisible =
            rect.top >= containerRect.top &&
            rect.bottom <= containerRect.bottom;

          if (isCompletelyVisible) {
            return hospital.portal_office_id;
          }
        }
        return null;
      },
      [hospitalListings],
    );

    // 一番上の表示アイテムを選択（フォールバック）
    const findTopVisibleItem = useCallback(
      (
        containerRect: DOMRect | { top: number; bottom: number },
      ): number | null => {
        if (!containerRef.current || !hospitalListings) return null;

        for (const hospital of hospitalListings) {
          if (!hospital.portal_office_id) continue;

          const element = containerRef.current.querySelector(
            `[data-hospital-id="${hospital.portal_office_id}"]`,
          );
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const isVisible =
            rect.bottom > containerRect.top && rect.top < containerRect.bottom;

          if (isVisible) {
            return hospital.portal_office_id;
          }
        }
        return null;
      },
      [hospitalListings],
    );

    const handleIntersection = useCallback(
      (_entries: IntersectionObserverEntry[]) => {
        if (!isShowMap || !setMapMarkerPortalOfficeIdWithoutScroll) {
          return;
        }

        // マーカークリックによるスクロール中は処理をスキップ
        if (isMarkerClickScrollRef.current) {
          return;
        }

        // デバウンス処理: 連続したイベントを制御
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          const scrollContainer = findScrollContainer();
          const containerRect = scrollContainer
            ? scrollContainer.getBoundingClientRect()
            : { top: 0, bottom: window.innerHeight };

          // 適切なアイテムを選択
          const selectedId =
            trySelectBottomItem(containerRect) ||
            findTopCompletelyVisibleItem(containerRect) ||
            findTopVisibleItem(containerRect);

          // 選択を更新
          if (selectedId !== null && selectedId !== lastSelectedIdRef.current) {
            lastSelectedIdRef.current = selectedId;
            setMapMarkerPortalOfficeIdWithoutScroll(selectedId);
          }
        }, 200); // 安定性を重視して200msに調整
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isShowMap,
        setMapMarkerPortalOfficeIdWithoutScroll,
        trySelectBottomItem,
        findTopCompletelyVisibleItem,
        findTopVisibleItem,
      ],
    );

    // Intersection Observerの設定
    useEffect(() => {
      if (
        !isShowMap ||
        !setMapMarkerPortalOfficeIdWithoutScroll ||
        !containerRef.current
      ) {
        // 既存のobserverがある場合は停止
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        // デバウンスタイマーもクリア
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }

      // スクロールコンテナを取得
      const scrollContainer = findScrollContainer();

      // Intersection Observerを作成（特定のスクロールコンテナ内での監視）
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: scrollContainer, // 特定のスクロールコンテナ内でのみ監視
        rootMargin: '0px',
        // より安定した閾値設定
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
      });

      // 現在の要素を監視対象に追加
      const elements =
        containerRef.current.querySelectorAll('[data-hospital-id]');
      elements.forEach((element) => {
        if (observerRef.current) {
          observerRef.current.observe(element);
        }
      });

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        // クリーンアップ時にタイマーもクリア
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
      };
    }, [
      isShowMap,
      setMapMarkerPortalOfficeIdWithoutScroll,
      hospitalListings,
      handleIntersection,
      findScrollContainer,
    ]);

    useImperativeHandle(ref, () => ({
      scrollToListing: (portalOfficeId: number, source?: 'click' | 'hover') => {
        if (!containerRef.current) return;

        // マーカークリックによるスクロールの場合、一時的にObserver処理を無効化
        if (source === 'click') {
          isMarkerClickScrollRef.current = true;

          // マーカークリックされたアイテムのIDを強制的に設定
          lastSelectedIdRef.current = portalOfficeId;
          setMapMarkerPortalOfficeIdWithoutScroll?.(portalOfficeId);

          // スクロール完了を検知してから無効化を解除
          const onScrollEnd = () => {
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
              isMarkerClickScrollRef.current = false;
            }, 1000); // スクロール停止から1秒後に解除
          };

          // スクロール開始前に既存のタイマーをクリア
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }

          // スクロール開始直後に一度呼び出す
          onScrollEnd();

          // 最大5秒後には必ず無効化を解除
          setTimeout(() => {
            isMarkerClickScrollRef.current = false;
          }, 5000);
        }

        const element = containerRef.current.querySelector(
          `[data-hospital-id="${portalOfficeId}"]`,
        );
        if (element) {
          element.scrollIntoView({
            behavior: source === 'click' ? 'smooth' : 'auto',
            block: 'center',
          });
        }
      },
    }));

    return (
      <Box ref={containerRef}>
        {hospitalListings?.map((listing) => (
          <Box
            key={listing.portal_office_id}
            mb={2}
            id={`hospital-${listing.portal_office_id}`}
            data-hospital-id={listing.portal_office_id}
          >
            <HospitalListingItem
              hospitalMedicalSubjects={hospitalMedicalSubjects}
              hospitalSearchFilters={hospitalSearchFilters}
              listingInfo={listing}
              isPublicSearch={isPublicSearch}
              requestUser={requestUser}
              localFavorites={localFavorites}
              setLocalFavorites={setLocalFavorites}
              customLink={getCustomLink(listing)}
              mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
              isShowMap={isShowMap}
            />
          </Box>
        ))}
      </Box>
    );
  },
);
